// 스테이지 진행 저장(dungeonhero.stage.v1, D3a): 차례로 깬 스테이지 수·지역별 고른 스테이지·스테이지별 클리어 수·지금 마을·이동 연출 대기.
// 기존 저장(save.v1 보상·지역 기록, profile.v2, growth.v2, gear.v1)과 키를 나눠 그 형식은 바꾸지 않는다(이전 빌드로 되돌려도 그대로 읽힌다).
//
// 이관(키가 없을 때 한 번): save.v1 지역 기록에서 진행을 만든다. 이전 빌드에서 맵 한 판 = 그 지역 보스(n-10) 처치였으므로
// R2를 한 번이라도 깼으면 지역1·2 전부(20 = 3-1 해금, 마을③), R1만 깼으면 지역1 전부(10 = 2-1 해금, 마을②), 없으면 0(1-1).
// 그래서 기존 플레이어의 강화 상한(마을②③ +9)과 다음 문이 이전 빌드와 같다(game.highestTown이 이 값을 쓴다).
//
// 쓰기: 저장소 최신을 다시 읽어 합친다(깬 수·스테이지별 클리어는 큰 쪽, 고른 스테이지·마을은 이 탭 값). 손상 원문은 .bak에 남기고 기록에서 다시 만든다.
// 미래 버전(v > 1)은 읽기만 한다(덮어쓰지 않는다). 쓰기는 판 끝·마을 명령에서만(고정 스텝 프레임 루프 밖의 사건 단위).
import { REGION_IDS, TOWN_COUNT, type RegionId } from '../shared/levels';
import { STAGES_PER_REGION } from '../shared/content/world';
import {
  LAST_STAGE_ORDINAL, applyClear, frontierStage, highestTownOf, isUnlocked, parseStage, regionFrontierIndex, regionUnlocked, stageIdOf, stageOrdinal,
} from '../shared/content/stages';
import type { StageId } from '../shared/content/types';
import type { RegionRecordView, StageProgressView } from '../shared/types';
import type { SaveStorage } from './save';
import { mergeOwed, parseOwedMap, type OwedReward } from './owedReward';

export const STAGE_KEY = 'dungeonhero.stage.v1';
export const STAGE_BACKUP_KEY = STAGE_KEY + '.bak';
export const STAGE_BACKUP2_KEY = STAGE_KEY + '.bak2';
export const STAGE_VERSION = 1;

export interface StageSave {
  v: 1;
  /** 차례로 깬 스테이지 수(0..LAST_STAGE_ORDINAL) */
  cleared: number;
  /** 지역별 고른 스테이지 번호(1..10). 없으면 그 지역 최전선 */
  selected: Partial<Record<RegionId, number>>;
  /** 스테이지별 클리어 수 */
  clears: Record<string, number>;
  /** 스테이지별 최고 별 */
  stars: Record<string, number>;
  /**
   * 받은 첫 클리어 보상(선택 필드, S3): `스테이지 id`(노말) 또는 `스테이지 id:hard|hell` → 1. 없는 저장(이전 빌드)도 정상으로 읽는다.
   * 이전 빌드가 이 필드를 지우고 다시 써도 노말 첫 클리어는 깬 수(cleared)로 판정되므로 중복 지급이 없다(claimedFirst)
   */
  claimed: Record<string, 1>;
  /**
   * 받는 중인 첫 클리어 보상(선택 필드, PR #175 리뷰): 클리어 순간 보상 내용(장부 연산·골드·영수증 id)을 적어 두고, 장비 장부 저장과
   * 결과 영수증 저장이 확인되면 claimed로 바꾸며 지운다. 재접속 때 남아 있으면 같은 연산 id로 다시 장부에 넣는다(owedReward.ts)
   */
  owed: Record<string, OwedReward>;
  /** 지금 머무는 마을(1..TOWN_COUNT) */
  town: number;
  /** n-10 첫 클리어 뒤 아직 보여 주지 않은 이동 연출의 도착 마을(0 = 없음) */
  travel: number;
  /**
   * 마을 명시 이동 순번(도착·워프마다 1 증가). 여러 탭 합치기에서 마을은 순번이 큰 쪽(마지막으로 실제로 옮긴 탭)을 따른다 —
   * 마을을 옮긴 적 없는 오래된 탭이 쓴 낮은 마을이 새 마을을 덮어 지역 문이 사라지는 일이 없게(PR #160 리뷰 P0)
   */
  townSeq: number;
  /** 어디서 이관했는지('save.v1' 또는 '') — 시험·QA 기록 */
  from: string;
}

type Records = Readonly<Record<RegionId, RegionRecordView>>;
type Read = { kind: 'none' } | { kind: 'ok'; data: StageSave } | { kind: 'corrupt'; raw: string } | { kind: 'future'; data: StageSave | null } | { kind: 'unavailable' };

function int(v: unknown, lo: number, hi: number, dflt: number): number {
  return typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi ? v : dflt;
}
/** 첫 클리어 기록 키: 노말 '<스테이지 id>', 하드·헬 '<스테이지 id>:hard|hell' */
export function claimKey(id: StageId, diff: 'normal' | 'hard' | 'hell' = 'normal'): string { return diff === 'normal' ? id : `${id}:${diff}`; }
function validClaimKey(k: string): boolean {
  const [id, diff, extra] = k.split(':');
  return extra === undefined && parseStage(id) !== null && (diff === undefined || diff === 'hard' || diff === 'hell');
}
function obj(v: unknown): v is Record<string, unknown> { return v !== null && typeof v === 'object' && !Array.isArray(v); }

/**
 * save.v1 지역 기록 → 깬 스테이지 수(위 이관 규칙, PR #160 리뷰 P2 보강).
 * 보상 영수증의 스테이지 id(이 빌드의 스테이지 판)가 있으면 그 최고 서수까지 깬 것으로 보고(순차 해금이라 앞은 모두 깬 것),
 * 지역 기록의 클리어 수에서 스테이지 판 수를 뺀 나머지만 '이전 빌드 판'(맵 한 판 = 그 지역 보스 처치)으로 센다 —
 * 그래서 이 빌드에서 1-1만 깬 계정을 지역1 완료(10)로 올리지 않는다. 이전 빌드 판·자유 입장 바닥값: R1 → 10, R2·R3 → 20, R4 → 30.
 */
export function clearedFromRecords(records: Records | undefined, stages: readonly string[] = []): number {
  const perRegion = REGION_IDS.map(() => 0);
  let best = 0;
  for (const id of stages) {
    const p = parseStage(id);
    if (!p) continue;
    perRegion[p.region - 1]++;
    best = Math.max(best, stageOrdinal(id));
  }
  if (!records) return best;
  const legacy = (id: RegionId, i: number): boolean => (records[id] ? records[id].clears - perRegion[i] > 0 : false);
  let floor = 0;
  if (legacy('r1', 0)) floor = STAGES_PER_REGION;
  if (legacy('r2', 1) || legacy('r3', 2)) floor = 2 * STAGES_PER_REGION;
  if (legacy('r4', 3)) floor = 3 * STAGES_PER_REGION;
  return Math.max(best, floor);
}

export function freshStage(cleared = 0, from = ''): StageSave {
  const c = Math.max(0, Math.min(LAST_STAGE_ORDINAL, cleared));
  return { v: 1, cleared: c, selected: {}, clears: {}, stars: {}, claimed: {}, owed: {}, town: highestTownOf(c, TOWN_COUNT), travel: 0, townSeq: 0, from };
}

/** 저장 원문 → 검증된 진행(형식이 틀린 필드는 버리고, 필수 필드가 틀리면 null) */
export function parseStageSave(raw: unknown): StageSave | null {
  if (!obj(raw) || raw.v !== 1) return null;
  const cleared = int(raw.cleared, 0, LAST_STAGE_ORDINAL, -1);
  if (cleared < 0) return null;
  const out = freshStage(cleared, typeof raw.from === 'string' ? raw.from : '');
  if (obj(raw.selected)) for (const id of REGION_IDS) {
    const s = int(raw.selected[id], 1, STAGES_PER_REGION, 0);
    if (s > 0) out.selected[id] = s;
  }
  for (const key of ['clears', 'stars'] as const) {
    const src = raw[key];
    if (!obj(src)) continue;
    for (const [k, v] of Object.entries(src)) if (parseStage(k) && int(v, 0, key === 'stars' ? 3 : 1e9, -1) >= 0) out[key][k] = v as number;
  }
  if (obj(raw.claimed)) for (const [k, v] of Object.entries(raw.claimed)) if (v === 1 && validClaimKey(k)) out.claimed[k] = 1;
  out.owed = parseOwedMap(raw.owed, (k) => out.claimed[k] !== 1 && parseStage(k) !== null);
  out.town = int(raw.town, 1, TOWN_COUNT, out.town);
  out.travel = int(raw.travel, 0, TOWN_COUNT, 0);
  out.townSeq = int(raw.townSeq, 0, Number.MAX_SAFE_INTEGER, 0);
  // 지금 마을은 도착한 가장 높은 마을을 넘지 않는다
  out.town = Math.min(out.town, highestTownOf(out.cleared, TOWN_COUNT));
  return out;
}

/**
 * 두 진행 합치기: 깬 수·스테이지별 기록은 큰 쪽, 고른 스테이지는 local 우선, 마을은 명시 이동 순번이 큰 쪽(같으면 높은 마을).
 * 이동 대기도 순번이 큰 쪽 것만 남긴다(같으면 큰 값): 순번이 큰 쪽은 그 뒤의 이동(도착·워프)으로 대기를 이미 소비·취소했으므로,
 * 순번이 낮은 탭의 메모리에 남은 낡은 대기가 플레이어가 직접 고른 마을을 덮어 다시 옮기지 않는다(PR #160 리뷰 P2, c018)
 */
export function mergeStage(base: StageSave, local: StageSave): StageSave {
  const out = freshStage(Math.max(base.cleared, local.cleared), base.from || local.from);
  out.selected = { ...base.selected, ...local.selected };
  out.claimed = { ...base.claimed, ...local.claimed };
  out.owed = mergeOwed(base.owed, local.owed, out.claimed);
  for (const key of ['clears', 'stars'] as const) {
    for (const src of [base[key], local[key]]) for (const [k, v] of Object.entries(src)) out[key][k] = Math.max(out[key][k] ?? 0, v);
  }
  const town = local.townSeq > base.townSeq ? local.town : base.townSeq > local.townSeq ? base.town : Math.max(local.town, base.town);
  out.town = Math.min(town, highestTownOf(out.cleared, TOWN_COUNT));
  out.townSeq = Math.max(local.townSeq, base.townSeq);
  // 이동 대기는 지금 마을보다 높을 때만 남는다(이 탭이 이미 도착했으면 소비된 것)
  const travel = local.townSeq > base.townSeq ? local.travel : base.townSeq > local.townSeq ? base.travel : Math.max(local.travel, base.travel);
  out.travel = travel > out.town ? travel : 0;
  return out;
}

export class StageProgressStore implements StageProgressView {
  private data: StageSave;
  /** 'ok' | 'future'(읽기 전용) */
  mode: 'ok' | 'future' = 'ok';
  revision = 0;
  lastSaveOk = true;
  /** 손상 원문을 .bak에 남기고 새로 만든 횟수 */
  recovered = 0;

  constructor(private readonly storage: SaveStorage, private readonly legacy: () => Records | undefined, private readonly evidence: () => readonly string[] = () => []) {
    this.data = freshStage();
    this.load();
  }

  get cleared(): number { return this.data.cleared; }
  get town(): number { return this.data.town; }
  get highestTown(): number { return highestTownOf(this.data.cleared, TOWN_COUNT); }
  get travel(): number { return this.data.travel; }
  get migratedFrom(): string { return this.data.from; }
  get frontier(): StageId { return frontierStage(this.data.cleared); }
  /** 마지막 스테이지(4-10)까지 다 깼는지 */
  get complete(): boolean { return this.data.cleared >= LAST_STAGE_ORDINAL; }
  stageClears(id: StageId): number { return this.data.clears[id] ?? 0; }
  stageStars(id: StageId): number { return this.data.stars[id] ?? 0; }
  /**
   * 첫 클리어 보상을 이미 받았는지. 노말은 이전 빌드에서 이미 깬 스테이지(서수 ≤ 깬 수)도 받은 것으로 친다 —
   * 이전 빌드가 쓴 저장에는 claimed가 없으므로 중복 지급을 막는다(롤백 뒤 다시 올라와도 같다)
   */
  claimedFirst(id: StageId, diff: 'normal' | 'hard' | 'hell' = 'normal'): boolean {
    if (this.data.claimed[claimKey(id, diff)] === 1 || this.data.owed[claimKey(id, diff)]) return true;
    return diff === 'normal' && stageOrdinal(id) <= this.data.cleared;
  }
  unlocked(id: StageId): boolean { return isUnlocked(this.data.cleared, id); }
  regionUnlocked(region: RegionId): boolean { return regionUnlocked(this.data.cleared, REGION_IDS.indexOf(region) + 1); }
  /** 그 지역에서 들어갈 스테이지(고른 스테이지가 열려 있으면 그것, 아니면 지역 최전선). 지역이 잠겼으면 null */
  selected(region: RegionId): StageId | null {
    const no = REGION_IDS.indexOf(region) + 1;
    const max = regionFrontierIndex(this.data.cleared, no);
    if (max <= 0) return null;
    const s = this.data.selected[region];
    return stageIdOf(no, s && s <= max ? s : max);
  }

  private read(): Read {
    let raw: string | null;
    try { raw = this.storage.getItem(STAGE_KEY); } catch { return { kind: 'unavailable' }; }
    if (raw === null) return { kind: 'none' };
    let parsed: unknown;
    try { parsed = JSON.parse(raw); } catch { return { kind: 'corrupt', raw }; }
    // 미래 버전: 아는 필드만 읽기 전용으로 읽는다(쓰지 않음) — 이 세션 강화 상한·문이 마을①로 떨어지지 않게(PR #160 리뷰 P2)
    if (obj(parsed) && typeof parsed.v === 'number' && parsed.v > STAGE_VERSION) return { kind: 'future', data: parseStageSave({ ...parsed, v: 1 }) };
    const data = parseStageSave(parsed);
    return data ? { kind: 'ok', data } : { kind: 'corrupt', raw };
  }

  /** 처음 읽기: 없으면 save.v1 기록에서 이관해 바로 쓴다 */
  private load(): void {
    const r = this.read();
    if (r.kind === 'ok') { this.data = r.data; this.heal(); return; }
    if (r.kind === 'future') { this.mode = 'future'; if (r.data) this.data = r.data; return; }
    if (r.kind === 'unavailable') return;
    if (r.kind === 'corrupt') {
      // 손상 원문 백업: 첫 손상(.bak)은 덮어쓰지 않고, 그 뒤 손상은 .bak2에 최신만 둔다(PR #160 리뷰 P2)
      try {
        const first = this.storage.getItem(STAGE_BACKUP_KEY);
        if (first === null) this.storage.setItem(STAGE_BACKUP_KEY, r.raw);
        else if (first !== r.raw) this.storage.setItem(STAGE_BACKUP2_KEY, r.raw);
      } catch { /* 백업 실패해도 진행은 기록에서 다시 만든다 */ }
      this.recovered++;
    }
    const cleared = clearedFromRecords(this.legacy(), this.evidence());
    this.data = freshStage(cleared, cleared > 0 ? 'save.v1' : '');
    this.write();
  }

  /**
   * 이미 깨진 저장 고치기(PR #160 리뷰 P0): 마을을 명시로 옮긴 기록이 없는데(townSeq 0) 지금 마을이 도착한 마을보다 낮고 이동 대기도 없으면,
   * 다음 마을 진입 때 도착한 마을로 가는 이동을 다시 예약한다(옛 합치기 규칙이 남긴 저장).
   */
  private heal(): void {
    const d = this.data, top = highestTownOf(d.cleared, TOWN_COUNT);
    if (d.townSeq === 0 && d.town < top && d.travel === 0) { d.travel = top; this.write(); }
  }

  /** 다른 탭이 쓴 진행을 읽어 합친다(맵 이동마다) */
  refresh(): void {
    const r = this.read();
    if (r.kind === 'ok') { const before = this.data.cleared; this.data = mergeStage(r.data, this.data); if (this.data.cleared !== before) this.revision++; }
    else if (r.kind === 'future') { this.mode = 'future'; if (r.data) { this.data = mergeStage(r.data, this.data); this.revision++; } }
  }

  private write(): void {
    if (this.mode !== 'ok') { this.lastSaveOk = false; return; }
    const r = this.read();
    if (r.kind === 'ok') this.data = mergeStage(r.data, this.data);
    else if (r.kind === 'future') { this.mode = 'future'; this.lastSaveOk = false; return; }
    try {
      const text = JSON.stringify(this.data);
      this.storage.setItem(STAGE_KEY, text);
      this.lastSaveOk = this.storage.getItem(STAGE_KEY) === text;
    } catch { this.lastSaveOk = false; }
    this.revision++;
  }

  /**
   * 스테이지 클리어 기록. 순서대로 다음 스테이지를 처음 깨면 진행이 오르고, n-10 첫 클리어면 다음 마을 이동 연출을 예약한다.
   * 결과: first(첫 클리어), regionDone(이번 첫 클리어가 n-10), travel(예약한 도착 마을, 0 = 없음),
   * rewardDue(첫 클리어 보상을 이번에 줘야 함 — 같은 쓰기에서 받은 것으로 기록한다)
   */
  recordClear(id: StageId, stars: number, owed?: OwedReward): { first: boolean; regionDone: boolean; travel: number; rewardDue: boolean } {
    if (!parseStage(id)) return { first: false, regionDone: false, travel: 0, rewardDue: false };
    this.refresh();
    const rewardDue = !this.claimedFirst(id);
    // 보상 내용이 딸려 오면 받는 중(owed)으로 적고 수령(claimed)은 settle이 저장 확인 뒤에 쓴다. 딸려 오지 않으면(보상 없음) 바로 수령
    if (rewardDue) { if (owed) this.data.owed[claimKey(id)] = owed; else this.data.claimed[claimKey(id)] = 1; }
    const res = applyClear(this.data.cleared, id);
    this.data.cleared = res.cleared;
    this.data.clears[id] = (this.data.clears[id] ?? 0) + 1;
    this.data.stars[id] = Math.max(this.data.stars[id] ?? 0, Math.max(0, Math.min(3, Math.floor(stars))));
    let travel = 0;
    if (res.regionDone) {
      const to = highestTownOf(res.cleared, TOWN_COUNT);
      if (to > this.data.town) { travel = to; this.data.travel = to; }
    }
    // 다음 스테이지를 자동으로 고른다(같은 지역 안이면 다음 번호)
    const p = parseStage(id)!;
    if (res.first && p.index < STAGES_PER_REGION) this.data.selected[REGION_IDS[p.region - 1]] = p.index + 1;
    this.write();
    return { first: res.first, regionDone: res.regionDone, travel, rewardDue };
  }

  /** 실패한 쓰기 다시 하기(결과 화면 재시도, PR #175 3차 리뷰). 저장됐으면 true */
  retrySave(): boolean {
    this.write();
    return this.lastSaveOk;
  }

  /** 지역의 스테이지 고르기(열린 스테이지만). 성공하면 true */
  select(region: RegionId, index: number): boolean {
    const no = REGION_IDS.indexOf(region) + 1;
    if (no < 1 || !Number.isInteger(index) || index < 1 || index > regionFrontierIndex(this.data.cleared, no)) return false;
    if (this.data.selected[region] === index) return true;
    this.data.selected[region] = index;
    this.write();
    return true;
  }

  /**
   * 마을 이동(도착한 마을까지만). 이동 대기가 그 마을 이하면 소비한다. explicit(플레이어 워프)면 남은 이동 대기도 취소한다 —
   * 직접 고른 마을이 이기고, 더 높은 마을은 워프 칸으로 갈 수 있다. 성공하면 true.
   * 저장소 최신을 먼저 합친다(PR #160 리뷰 P2, c018): 다른 탭이 그사이 마을을 옮겼으면(순번 증가) 이 탭의 순번이 그보다 커야
   * 이번 이동이 합치기에서 이긴다 — 낡은 순번 + 1이 다른 탭의 워프와 같아져 높은 마을 쪽으로 조용히 되돌려지지 않게
   */
  setTown(town: number, explicit = false): boolean {
    if (!Number.isInteger(town) || town < 1) return false;
    this.refresh();
    if (town > this.highestTown) return false;
    const consume = this.data.travel !== 0 && (explicit || this.data.travel <= town);
    if (this.data.town === town && !consume) return true;
    this.data.town = town;
    this.data.townSeq++;
    if (consume) this.data.travel = 0;
    this.write();
    return true;
  }

  /** 첫 클리어 보상을 줘야 하는 판인가(미리 판정, 기록하지 않는다 — flow가 골드를 결과에 합산할 때 쓴다) */
  firstRewardDue(id: StageId, diff: 'normal' | 'hard' | 'hell' = 'normal'): boolean {
    this.refresh();
    return parseStage(id) !== null && !this.claimedFirst(id, diff);
  }

  /** 받는 중인 첫 클리어 보상이 있는가(저장 확인 대기, 틱마다 불러도 할당이 없다) */
  get hasOwed(): boolean { for (const k in this.data.owed) if (this.data.owed[k]) return true; return false; }
  /** 받는 중인 첫 클리어 보상 목록(재시도 때만 부른다) */
  owedEntries(): [string, OwedReward][] { return Object.entries(this.data.owed); }
  /** 받는 중인 보상을 수령으로 확정한다(호출자가 장비 장부·결과 영수증 저장을 확인한 뒤). 영속됐으면 true, 아니면 되돌리고 false */
  settleOwed(key: string): boolean {
    const prev = this.data.owed[key];
    if (!prev) return true;
    this.refresh();
    this.data.claimed[key] = 1;
    delete this.data.owed[key];
    this.write();
    if (this.lastSaveOk) return true;
    // 저장 실패(쓰기 오류·읽기 전용): 메모리도 받는 중으로 되돌려 다음 주기에 다시 시도한다(호출자는 이 동안 골드를 주지 않는다)
    delete this.data.claimed[key];
    this.data.owed[key] = prev;
    return false;
  }

  /** 시험·QA: 저장된 원문 */
  snapshot(): StageSave { return JSON.parse(JSON.stringify(this.data)) as StageSave; }
}

/** 시험·QA 픽스처: 진행 원문을 만든다(서수 cleared까지 깬 상태) */
export function stageFixture(cleared: number, extra: Partial<StageSave> = {}): string {
  return JSON.stringify({ ...freshStage(cleared), ...extra });
}
export { stageOrdinal };
